'use client';

/**
 * Visual baseline for course tiles. 16:9 thumbnail with an optional
 * progress bar, a solid participant-count badge over the image, soft
 * hover lift. Used as the fallback when {@code cardStyle} isn't set or
 * when a {@code 'custom'} theme primitive isn't registered.
 */
import { PortalLink } from '@akademi/site-builder-runtime';
import { Card } from '@akademi/ui';
import { LevelBadge } from '../_shared/LevelBadge';
import { CourseProgressBar } from '../_shared/CourseProgressBar';
import { CourseCoverImage } from '../_shared/CourseCoverImage';
import { cardReset } from '../_shared/styles';
import type { CourseCardProps } from './CourseCard.types';
import type { CardGridSpec } from './gridSpecs';
import type { ProgressTextPlace } from './registry';

// Level + participant badges are revealed on hover/focus only — pure CSS
// so neither needs JS state just to toggle an opacity.
//
// The lift+shadow lives on `.lms-default-card-wrap` (the OUTER element)
// rather than on `.lms-default-card` (the inner, `overflow: hidden` anchor
// that clips the image's rounded corners) on purpose: a box-shadow painted
// on the same box that also has `overflow: hidden` gets clipped by that
// overflow in most engines, so the hover-lift shadow would render
// partially or not at all. Splitting the two elements keeps the shadow
// intact while the inner element still clips the image/badges to the
// card's rounded corners.
const DEFAULT_CARD_CSS = `
.lms-default-card-wrap {
  border-radius: var(--radius-md, 12px);
  box-shadow: var(--shadow-xs, 0 1px 2px rgba(0,0,0,0.08));
  transition: transform 200ms ease, box-shadow 200ms ease;
}
.lms-default-card-wrap:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 24px rgba(0,0,0,0.13), 0 2px 6px rgba(0,0,0,0.06);
}
.lms-default-card-badge { opacity: 0; transition: opacity 160ms ease; pointer-events: none; }
.lms-default-card:hover .lms-default-card-badge,
.lms-default-card:focus-visible .lms-default-card-badge { opacity: 1 !important; }
@media (prefers-reduced-motion: reduce) {
  .lms-default-card-wrap,
  .lms-default-card-badge { transition: none; }
}
`;

const cardSurface = {
  background: 'var(--color-surface-card, #ffffff)',
  borderRadius: 'inherit',
  border: '1px solid var(--color-border-subtle, #e5e7eb)',
};

function UsersIcon({ size = 14 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

export function DefaultCourseCard({
  course: c,
  progressBar: {
    show: showProgress = false,
    showText: showProgressText = false,
    textPlace: progressTextPlace = 'outside-right',
  } = {},
}: {
  course: CourseCardProps;
  progressBar?: { show?: boolean; showText?: boolean; textPlace?: ProgressTextPlace };
}) {
  return (
    <div className="lms-default-card-wrap">
      <style dangerouslySetInnerHTML={{ __html: DEFAULT_CARD_CSS }} />
      <Card asChild>
        <PortalLink
          href={c.href}
          className="lms-default-card"
          style={{
            ...cardReset,
            ...cardSurface,
            overflow: 'hidden',
            display: 'block',
            color: 'inherit',
            textDecoration: 'none',
          }}
        >
          <div
            style={{
              aspectRatio: 'var(--app-course-image-aspect-ratio, 1.6)',
              backgroundColor: 'var(--color-neutral-100, #f4f4f5)',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <CourseCoverImage
              image={typeof c.image === 'string' ? c.image : undefined}
              resizeTo="480x0"
            />
            <LevelBadge
              level={c.level}
              variant="solid"
              className="lms-default-card-badge"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                borderBottomRightRadius: 10,
              }}
            />
            {typeof c.enrolled === 'number' && c.enrolled > 0 && (
              <div
                className="lms-default-card-badge"
                style={{
                  position: 'absolute',
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '5px 40px',
                  opacity: 0.9,
                  borderTopLeftRadius: 10,
                  background: 'var(--color-brand-600, #4f6fdb)',
                  color: '#fff',
                  fontSize: 16,
                  fontWeight: 500,
                }}
              >
                <UsersIcon size={18} />
                {c.enrolled.toLocaleString()}
              </div>
            )}
          </div>
          <div style={{ padding: 'clamp(12px, 1.6vw, 16px)' }}>
            <h3
              style={{
                margin: 0,
                fontSize: 'clamp(14px, 1.7vw, 16px)',
                fontWeight: 600,
                lineHeight: 1.35,
                color: 'var(--color-text-primary, #0f1218)',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                minHeight: 'calc(1.35em * 2)',
              }}
            >
              {c.title}
            </h3>
          </div>
          {showProgress && typeof c.progress === 'number' && (
            <div className="lms-default-card-progress" style={{ padding: '0 8px 8px 8px' }}>
              <CourseProgressBar
                progress={c.progress}
                showText={showProgressText}
                textPlace={progressTextPlace}
              />
            </div>
          )}
        </PortalLink>
      </Card>
    </div>
  );
}

DefaultCourseCard.gridSpec = {
  kind: 'grid',
  minCardWidth: 220,
  gap: 'clamp(12px, 2vw, 16px)',
} satisfies CardGridSpec;
